import React, { useEffect } from "react";
import { COMPONENT, FORMDATA, SELECT, TEXT } from "../../constants/constants";
import { API_MANUAL_WITHDRAWALS, API_USER_BANK_ACCOUNTS, API_USER_MOMO_ACCOUNTS } from "../../constants/apis";
import useGet from "../common/useGet";
import { TSet } from "../common/useSet";
import { showConfetti } from "../../components/elements/ConfettiView";
import { reloadData } from "../../components/lists/ListHandler";
import { InfoBox } from "../../components/elements/Elements";
import { PropOptions } from "../../utils/formUtils";
import { transactionPinField } from "../../utils/pinUtils";
import { currencyFormat } from "../../utils/helpers";
import { useWallet } from "../wallets/useWallet";

export const useWithdrawalForm = ({ form }: any) => {
  const { getWalletBalance, balance } = useWallet();
  const { loadData: loadBanks, data: banksRaw, loading: loadingBanks } = useGet();
  const { loadData: loadMomos, data: momosRaw } = useGet();

  useEffect(() => {
    loadBanks({ api: API_USER_BANK_ACCOUNTS });
    loadMomos({ api: API_USER_MOMO_ACCOUNTS });
  }, []);

  const banks: any[] = Array.isArray(banksRaw) ? banksRaw : (banksRaw?.results || []);
  const momos: any[] = Array.isArray(momosRaw) ? momosRaw : (momosRaw?.results || []);
  const method = form?.method || "bank";
  const balanceNum = parseInt(balance || "0", 10);

  const bankOptions = banks.map((b: any) => ({
    id: b.id,
    name: b.bank_name + " · " + b.account_number + " (" + b.account_name + ")",
  }));

  const momoHelper = momos.length > 0
    ? "Saved: " + momos[0].phone_number + " (" + (momos[0].provider_display || momos[0].provider) + ")"
    : "Enter your MoMo number";

  const amtDisplay = form?.amount ? currencyFormat(parseFloat(form.amount)) : "the amount";
  const destLabel = method === "bank" ? " bank account" : " MoMo number";

  const fields: any[] = [
    {
      type: COMPONENT,
      component: (
        <div className="textCenter flexColumn gap5">
          <div className="font16 bold textDark">Request Withdrawal</div>
          <div className="font12 grey">We will send money to your bank account or mobile money</div>
        </div>
      ),
    },
    {
      type: COMPONENT,
      component: (
        <div className="lightBg r10 p12 textCenter">
          <div className="font11 grey">Withdraw From</div>
          <div className="font16 bold textDark">
            Wallet · {currencyFormat(parseInt(balance))}
          </div>
        </div>
      ),
    },
    {
      label: "Withdrawal Amount",
      field: "amount",
      type: TEXT,
      required: true,
      props: {
        ...PropOptions.number,
        prefix: "UGX",
        helperText: "Available: " + currencyFormat(balanceNum),
      },
    },
    {
      field: "method",
      label: "Send To",
      type: SELECT,
      required: true,
      options: [
        { id: "bank", name: "Bank Account" },
        { id: "momo", name: "Mobile Money" },
      ],
    },
    {
      field: "bank_account",
      label: "Select Bank Account",
      type: SELECT,
      visible: method === "bank" && bankOptions.length > 0,
      required: method === "bank",
      options: bankOptions,
      props: { loading: loadingBanks },
    },
    {
      type: COMPONENT,
      visible: method === "bank" && bankOptions.length === 0 && !loadingBanks,
      component: (
        <InfoBox color="#ed6c02">
          No bank accounts on file. Go to Account &gt; Payment Methods to add one, or choose Mobile Money.
        </InfoBox>
      ),
    },
    {
      field: "momo_number",
      label: "Mobile Money Number",
      type: TEXT,
      visible: method === "momo",
      required: method === "momo",
      props: { placeholder: "+256700123456", helperText: momoHelper },
    },
    transactionPinField,
    {
      type: COMPONENT,
      visible: !!form?.amount && parseFloat(form?.amount) > 0,
      component: (
        <InfoBox color="#f59e0b">
          {"Your withdrawal request will be reviewed. Once approved, " + amtDisplay + " will be deducted from your wallet and sent to your" + destLabel + ". This typically takes 1–4 business hours."}
        </InfoBox>
      ),
    },
  ];

  const onSubmit = ({ payload }: any): TSet => {
    const fd = new FormData();
    fd.append("amount", payload.amount);
    if (payload.pin) fd.append("pin", payload.pin);
    if (payload.method === "bank" && payload.bank_account) {
      const bank = banks.find((b: any) => b.id === payload.bank_account);
      fd.append("bank", payload.bank_account);
      const dest = bank
        ? bank.bank_name + " - " + bank.account_number + " (" + bank.account_name + ")"
        : "Bank account";
      fd.append("destination", dest);
    } else if (payload.method === "momo" && payload.momo_number) {
      fd.append("destination", payload.momo_number);
    }
    return {
      api: API_MANUAL_WITHDRAWALS,
      params: fd,
      type: FORMDATA as any,
      successMessage: { message: "Withdrawal request submitted!", duration: 7 },
      onSuccess: () => {
        reloadData("manual_withdrawals");
        reloadData("transactions");
        showConfetti();
        setTimeout(() => getWalletBalance(), 2000);
      },
    };
  };

  const hasDestination = (method === "bank" && !!form?.bank_account) || (method === "momo" && !!form?.momo_number);
  const amtValid = !!form?.amount && parseFloat(form?.amount) > 0 && parseFloat(form?.amount) <= balanceNum;

  return {
    fields,
    onSubmit,
    title: "Withdraw Money",
    submitText: "Request Withdrawal",
    allowSubmit: amtValid && hasDestination,
    defaultValues: { method: "bank", momo_number: momos?.[0]?.phone_number || "" },
  };
};